/** Every published privacy body stays renderable at its versioned URL and for a
 * staged checkout/API rollout. A handoff must display the exact version its API
 * asks it to record. Add a version only together with its publication migration
 * and its body in privacy-notice-text/ (every locale; assembled by compose.ts). */
export const PUBLIC_PRIVACY_NOTICE_VERSIONS = ['1.1', '1.2', '1.3', '1.4'] as const
export type PublicPrivacyNoticeVersion = typeof PUBLIC_PRIVACY_NOTICE_VERSIONS[number]

export function isPublicPrivacyNoticeVersion(version: unknown): version is PublicPrivacyNoticeVersion {
  return typeof version === 'string' && (PUBLIC_PRIVACY_NOTICE_VERSIONS as readonly string[]).includes(version)
}

/**
 * Product retention and access limits, not assertions about statutory periods.
 * Operational contact/media and retained financial evidence have separate lives.
 * SQL mirrors these bounds; behavioral tests must keep both authorities aligned.
 */
export const CUSTOMER_PRIVACY_POLICY = {
  noticeVersion: '1.4' satisfies PublicPrivacyNoticeVersion,
  // v1.3 clarifies provider/partner identity processing only. v1.4 (2026-10-28)
  // describes existing processing more completely (Decor, finished-print photos,
  // preview copies, sign-in codes, saved addresses, Doodles self-service deletion)
  // and names no new processor or processing country. Existing customer country
  // assessments therefore retain their original version, scope and expiry.
  compatibleCustomerNoticeVersions: ['1.1', '1.2', '1.3', '1.4'] as readonly string[],
  reviewVersion: 'customer-data-v1',
  operationalRetentionDays: 180,
  unpaidRetentionMaxDays: 90,
  privateAccessDays: 30,
  verificationMinutes: 10,
  verificationAttempts: 5,
  verificationEmailCooldownSeconds: 60,
  verificationEmailsPerHour: 5,
  handoffHours: 24,
  reviewMaxDays: 365,
  reviewWarningDays: 30,
  existingMarketTransitionDays: 30,
  recoveryMaxAgeDays: 180,
  disputeEvidenceMinimumDays: 180,
} as const

export const CUSTOMER_PRIVACY_RETAINED_CATEGORIES = [
  'financial_records', 'active_fulfillment', 'open_dispute',
  'payment_resolution', 'legal_hold', 'backup_rotation',
] as const

export type CustomerPrivacyRetainedCategory =
  typeof CUSTOMER_PRIVACY_RETAINED_CATEGORIES[number]
